/**
 * Responsibility: assemble one tile's complete geometry — its terrain and every structure
 * standing on it — into a single pair of typed arrays.
 *
 * ## Why one mesh per tile rather than two
 *
 * Terrain and structures use the same material and the same per-vertex colour attribute, so
 * merging them costs nothing and keeps everything downstream unchanged: one draw call per tile,
 * one geometry to dispose, one bounding sphere. A separate structure mesh would double all
 * three for no benefit.
 *
 * ## Two passes, because the size has to be exact
 *
 * Structures are collected as descriptions first, then their segments counted, then the arrays
 * allocated and filled. Guessing an upper bound and trimming later would mean either
 * over-allocating on every tile or discovering mid-write that the estimate was low.
 *
 * Contains no Three.js and no DOM access.
 */

import { buildTile, segmentCountFor, surfaceTriangleCountFor } from './tile.js';
import { createGeometryWriter, createMaskWriter } from './structureGeometry.js';
import { kindOf } from './structureKinds.js';
import { hexToRgb } from './palette.js';
import { BIOMES } from './biomes/index.js';
import { STRUCTURE_PALETTE, OCCLUSION, BIOME, TILES } from '../config.js';
/** Floats per vertex, and vertices per segment, matching `tile.js`. */
const COMPONENTS_PER_VERTEX = 3;
const VERTICES_PER_SEGMENT = 2;
const INDICES_PER_TRIANGLE = 3;

/** Empty mask, for tiles that need no depth geometry at all. */
const NO_MASK = { positions: null, indices: null, triangleCount: 0 };


/**
 * Create the tile content builder.
 *
 * @param {object} options
 * @param {number} options.seed - Base world seed.
 * @param {number} options.size - Tile edge length in world units.
 * @param {number} options.resolution - Terrain vertices along one tile edge.
 * @param {{sampleDetail: Function, sampleSurface: Function}} options.heightfield
 * @param {{sample: Function}} options.palette - Terrain colour sampler.
 * @param {{sample: Function}} options.biomeField - Supplies per-biome weights for structure
 *   density.
 * @param {Array<object>} [options.biomes] - Biome descriptors. Defaults to the registry.
 * @returns {{build: (originX: number, originZ: number) => object, hasStructures: () => boolean}}
 */
export function createTileContent({
  seed,
  size,
  resolution,
  heightfield,
  palette,
  biomeField,
  biomes = BIOMES,

  /**
   * A claim on the ground that belongs to no biome, consulted by every one of them.
   *
   * The railway corridor, and so far only that. It is not a biome — it is a route laid *through* the biomes —
   * so it cannot arrive by declaring `createReservation` the way water and buildings do, but every biome
   * should keep off it for exactly the same reasons and through exactly the same door.
   *
   * Optional, and absent is the ordinary case: a world with no railway in it passes nothing here and pays one
   * comparison per query.
   */
  reserved = null,
}) {
  if (!heightfield || typeof heightfield.sampleDetail !== 'function') {
    throw new Error('createTileContent: heightfield must expose sampleDetail');
  }
  if (!heightfield || typeof heightfield.sampleSurface !== 'function') {
    throw new Error('createTileContent: heightfield must expose sampleSurface');
  }
  if (!heightfield || typeof heightfield.sampleSurfaceDetail !== 'function') {
    throw new Error('createTileContent: heightfield must expose sampleSurfaceDetail');
  }
  if (!palette || typeof palette.sample !== 'function') {
    throw new Error('createTileContent: palette must expose sample');
  }
  if (!biomeField || typeof biomeField.sample !== 'function') {
    throw new Error('createTileContent: biomeField must expose sample');
  }

  /*
    Only biomes that actually build things. Most do not, and this list is walked for every
    tile, so resolving it once keeps the common case free.
  */
  const builders = biomes
    .filter((biome) => typeof biome.createStructures === 'function')
    .map((biome) => ({
      id: biome.id,
      collect: biome.createStructures(seed),
      /*
        A biome may give its structures a gradient of their own. The forest does: its ground is
        deliberately muted so the trees carry the biome's identity, and sharing one gradient would
        leave both the same colour. The city uses one for both, which is the default.
      */
      gradient: {
        low: hexToRgb((biome.structurePalette ?? biome.palette).LOW),
        high: hexToRgb((biome.structurePalette ?? biome.palette).HIGH),
      },
    }));

  /*
    Structures sit on the *drawn* lattice surface, not the continuous field. Same reason the
    flight floor does: the two differ by up to a hundred units, which would leave buildings
    visibly hovering above or sunk into the wireframe they are standing on.
  */
  const sampleGround = (x, z) => heightfield.sampleSurface(x, z);

  /*
    Height and slope together. A biome that needs both — the forest, for rejecting cliff faces — pays
    for one set of samples instead of three.
  */
  const sampleGroundDetail = (x, z) => heightfield.sampleSurfaceDetail(x, z);

  /**
   * Biomes that declare where their structures sit, so other biomes can keep clear of them.
   *
   * Built once. Only the city declares one today: a tree inside a tower is the bug, while a tower in a
   * wood is fine, so buildings take precedence over trees rather than the reverse.
   *
   * A biome never sees its own reservation. Every biome already guarantees its own structures cannot
   * intersect — the city through street width, the forest through slot spacing — and self-consultation
   * would reject everything.
   */
  const reservations = biomes
    .filter((biome) => typeof biome.createReservation === 'function')
    .map((biome) => {
      const test = biome.createReservation(seed);

      return {
        id: biome.id,
        test,

        /*
          Whether this claim is on the ground itself rather than on something built on it. Water sets it; see
          `createWaterClearance`, and `isReserved` for what turns on it.
        */
        claimsGround: test.claimsGround === true,

        sampleWeight: (x, z) => weightAt(biome.id, x, z),

        /*
          The reservation has to reach the same verdict about where structures are as the placement code does, so
          anything placement decides from has to be available here too. The fairground sites itself on a peak in
          this field, so without it the reservation would guard a different set of sites than the one that gets
          built.
        */
        sampleSuitability: (x, z) => suitabilityAt(biome.id, x, z),
      };
    });

  /**
   * Biomes whose structures take precedence over other biomes' structures.
   *
   * Empty but for the fairground, and it is worth being clear about why that is not favouritism. Every other
   * structural biome is a *fill* that thins out at the edge of its region, so losing a block to a neighbour costs
   * it one shed and nothing notices. A fairground is a single site that is either complete or absent, so the same
   * collision costs the entire feature.
   *
   * Measured: of fifteen sites the fairground chose in a 128,000-unit square, eleven were refused — eight by
   * industrial and six by suburbia, both of which reserve a whole block wherever they have *any* weight at all and
   * would have built nothing on most of it.
   */
  const outranksStructures = new Set(
    biomes.filter((biome) => biome.outranksStructures === true).map((biome) => biome.id),
  );

  /**
   * Whether a point is inside another biome's reserved space.
   *
   * @param {Array<object>} active - Reservations worth consulting for this region. Biomes with no presence
   *   anywhere near it would answer false for every point, so they are filtered out once per tile rather
   *   than re-checked on every one of the hundreds of queries a tile makes.
   * @param {string} askingId - Biome asking, excluded from the check.
   * @param {number} x
   * @param {number} z
   * @param {number} radius
   * @param {Map} cache - Per-tile memo, shared across every reservation in one build.
   * @returns {boolean}
   */
  function isReserved(active, askingId, x, z, radius, cache) {
    const outranking = outranksStructures.has(askingId);

    /*
      **Checked first, and never skipped.** The railway belongs to no biome, so it is not in `active`; and it is
      a claim on the ground, so a biome that outranks other biomes' *structures* still cannot build on it.
      There is nothing on the other side of a railway to negotiate with, the same as a lake.
    */
    if (reserved !== null && reserved(x, z, radius)) return true;

    for (const reservation of active) {
      if (reservation.id === askingId) continue;

      /*
        **Ignoring another biome's structures cannot produce an overlap**, which is the only reason this is safe.
        Every structural biome consults this same function when it places, and the outranking biome publishes its
        own reservation, so the one being ignored is already keeping clear from the other side. What changes is
        only which of the two yields.
        
        Ground claims are never skipped. There is nothing on the other side of that negotiation — a lake is simply
        there — so a park that ignored one would stand in the water.
      */
      if (outranking && !reservation.claimsGround) continue;

      const context = {
        sampleWeight: reservation.sampleWeight,
        sampleSuitability: reservation.sampleSuitability,
        cache,
      };

      if (reservation.test(x, z, radius, context)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Biome ids with any plausible presence in or near a region.
   *
   * ## Why this is worth doing
   *
   * **Every biome's collector used to run on every tile.** Measured, structure collection was 38–58% of a
   * tile build while only three to eight of the thirteen biomes had any weight there — the other ten were
   * each walking their own grid of slots, sampling their own fields, to place nothing. Skipping them saved
   * 15–34% of a whole tile.
   *
   * ## Why it is judged on suitability rather than on weight
   *
   * Weight is the thing that matters, but it is also the thing that goes to *exactly* zero at a boundary, so
   * a test on it has no margin at all. Suitability is the smooth field underneath: a biome can only carry
   * weight when it is within `BLEND_WIDTH` of the leader, so anything further behind than that cannot
   * possibly appear. Keeping a biome alive until it is `SKIP_SAFETY` blend widths behind leaves a wide band
   * where a collector still runs and simply finds nothing.
   *
   * That margin matters most for **reservations**, not structures. A skipped structure at a weight of a few
   * percent is one that density would almost certainly have rejected anyway; a skipped *reservation* would
   * let another biome build in this one's water, which is a visible bug.
   *
   * ## Why it is safe between probes
   *
   * The suitability fields run at `BIOME.FIELD_WAVELENGTH` over `FIELD_OCTAVES`, so the finest feature in them
   * is thousands of units across, while the probe grid spans the tile plus a margin at a spacing of 1,200. A
   * region of presence therefore cannot hide between probes.
   *
   * **A biome may set its own, shorter wavelength**, and if one ever went far below this spacing the argument
   * above would stop holding. The fairground is the only one that does, at 11,000 — whose finest octave is still
   * some 5,000 units across, four times the probe spacing.
   *
   * Skipped entirely when the biome field has no `sampleSuitability`, which is how tests inject a fake one.
   * Doing nothing is the correct fallback: every collector runs, exactly as before.
   *
   * @param {{originX: number, originZ: number, size: number}} region
   * @returns {Set<string>|null} Null means "no idea, assume everything".
   */
  function biomesNear(region) {
    if (typeof biomeField.sampleSuitability !== 'function') return null;

    const margin = TILES.PRESENCE_MARGIN;
    const probes = TILES.PRESENCE_PROBES;
    const span = region.size + 2 * margin;
    const present = new Set();

    for (let i = 0; i < probes; i += 1) {
      const x = region.originX - margin + (span * i) / (probes - 1);

      for (let j = 0; j < probes; j += 1) {
        const z = region.originZ - margin + (span * j) / (probes - 1);
        const suitabilities = biomeField.sampleSuitability(x, z);

        let leader = Number.NEGATIVE_INFINITY;
        for (const entry of suitabilities) {
          if (entry.suitability > leader) leader = entry.suitability;
        }

        const cutoff = leader - BIOME.BLEND_WIDTH * BIOME.SKIP_SAFETY;
        for (const entry of suitabilities) {
          if (entry.suitability >= cutoff) present.add(entry.id);
        }
      }
    }

    return present;
  }

  /**
   * Ask every structural biome what it wants to place in a region.
   *
   * Extracted so `build` and the public `collectStructures` share one path. Cross-biome clearance only
   * works because the reservations are composed here, so a test that called a biome directly would be
   * testing something the game never does.
   *
   * @param {{originX: number, originZ: number, size: number}} region
   * @returns {Array<{builder: object, buildings: Array<object>}>}
   */
  function collectFor(region) {
    /*
      Fresh per region, so the memo inside a reservation lives exactly as long as the tile being built.
      A cache held for the session would grow without bound as the player flies.
    */
    const reservationCache = new Map();
    const collected = [];

    /*
      Which biomes could possibly matter here, worked out once for the whole tile. See `biomesNear` for why
      this is the single largest saving available in a tile build, and why it is judged with a wide margin.
    */
    const present = biomesNear(region);
    const active = present ? reservations.filter((one) => present.has(one.id)) : reservations;

    for (const builder of builders) {
      // A biome with no presence anywhere near this tile would walk its whole grid to place nothing.
      if (present && !present.has(builder.id)) continue;

      const buildings = builder.collect(region, {
        sampleWeight: (x, z) => weightAt(builder.id, x, z),

        /*
          Every biome's weight at a point, not just the asking biome's.

          For local rules about which biomes can share ground. Farmland is the case that needs it: a field
          inside a city is not a transition, it is a mistake, so farmland refuses to work land where a rival
          biome has any real presence. Read-only, and it changes nothing about the biome field itself —
          terrain height and colour still blend normally, only the *use* of the land stops.
        */
        sampleWeights: (x, z) => biomeField.sample(x, z),

        /*
          The asking biome's own raw suitability. See `suitabilityAt` for why weight cannot answer the question
          this exists for.
        */
        sampleSuitability: (x, z) => suitabilityAt(builder.id, x, z),

        sampleGround,
        sampleGroundDetail,
        isReserved: (x, z, radius) =>
          isReserved(active, builder.id, x, z, radius, reservationCache),
      });

      if (buildings.length > 0) collected.push({ builder, buildings });
    }

    return collected;
  }

  /** Accent colour is shared across biomes; unpacked once. */
  const accentColor = hexToRgb(STRUCTURE_PALETTE.ACCENT);

  /**
   * Mask holding only the terrain surface.
   *
   * The common case by a wide margin — most tiles have no structures — so it hands the arrays
   * `buildTile` already produced straight through rather than copying them into a combined
   * buffer for no reason.
   */
  function terrainMask(terrain) {
    if (!terrain.surfacePositions) return NO_MASK;

    return {
      positions: terrain.surfacePositions,
      indices: terrain.surfaceIndices,
      triangleCount: surfaceTriangleCountFor(resolution),
    };
  }

  /**
   * Mask holding the terrain surface plus every building, in one buffer pair.
   *
   * One mask mesh per tile rather than one per building: the draw call count is what matters
   * here, and these carry no colour so there is nothing to keep separate.
   */
  function combinedMask(terrain, collected) {
    const maskTerrain = Boolean(terrain.surfacePositions);

    /*
      Counted per structure rather than assumed, because not every kind casts a mask — trees
      deliberately do not. See `structureKinds.js` for why.
    */
    let structureVertices = 0;
    let structureTriangles = 0;

    if (OCCLUSION.MASK_STRUCTURES) {
      for (const { buildings } of collected) {
        for (const building of buildings) {
          const kind = kindOf(building);
          structureVertices += kind.maskVertices(building);
          structureTriangles += kind.maskTriangles(building);
        }
      }
    }

    const maskStructures = structureTriangles > 0;
    if (!maskTerrain && !maskStructures) return NO_MASK;
    if (!maskStructures) return terrainMask(terrain);

    const terrainVertices = maskTerrain ? terrain.surfacePositions.length / COMPONENTS_PER_VERTEX : 0;
    const terrainIndices = maskTerrain ? terrain.surfaceIndices.length : 0;
    const structureIndices = structureTriangles * INDICES_PER_TRIANGLE;

    const positions = new Float32Array(
      (terrainVertices + structureVertices) * COMPONENTS_PER_VERTEX,
    );
    const indices = new Uint32Array(terrainIndices + structureIndices);

    if (maskTerrain) {
      positions.set(terrain.surfacePositions, 0);
      indices.set(terrain.surfaceIndices, 0);
    }

    /*
      Structure vertices are appended after the terrain's, so their indices are offset by the
      terrain vertex count. Getting that offset wrong would have buildings referencing terrain
      vertices and produce triangles stretching across the whole tile.
    */
    const structurePositions = positions.subarray(terrainVertices * COMPONENTS_PER_VERTEX);
    const structureIndexBuffer = indices.subarray(terrainIndices);
    /*
      Structures are placed in absolute world coordinates but must be written tile-relative, so the
      writer is given the tile origin to subtract. The terrain surface copied in above is already
      tile-relative.
    */
    const writer = createMaskWriter(
      structurePositions,
      structureIndexBuffer,
      terrainVertices,
      terrain.originX,
      terrain.originZ,
    );

    for (const { buildings } of collected) {
      for (const building of buildings) {
        kindOf(building).emitMask(writer, building);
      }
    }

    return {
      positions,
      indices,
      triangleCount: indices.length / INDICES_PER_TRIANGLE,
    };
  }

  /** Weight of one biome at a coordinate, or 0 if it is not present there. */
  function weightAt(biomeId, x, z) {
    const weights = biomeField.sample(x, z);
    for (const entry of weights) {
      if (entry.id === biomeId) return entry.weight;
    }
    return 0;
  }

  /**
   * How much one biome *wants* a coordinate, before ranking against its rivals.
   *
   * Offered alongside the weight because the two answer different questions, and one case needs the raw
   * number. Weight saturates: across the interior of a region the leader's weight is exactly 1 over a wide
   * area, so a biome cannot use it to find where in its own region it is *most* at home — every point ties.
   * Suitability keeps varying there, so it has a single peak per region.
   *
   * The fairground is the biome that needs this. It puts exactly one park in a region, and the way it picks
   * which candidate site gets it is to take the one whose suitability beats its neighbours'. Nothing else
   * gives a region a unique, tile-local, agreed-upon centre.
   *
   * @returns {number} Unbounded, and only meaningful compared against the same biome elsewhere.
   */
  function suitabilityAt(biomeId, x, z) {
    const all = biomeField.sampleSuitability(x, z);
    for (const entry of all) {
      if (entry.id === biomeId) return entry.suitability;
    }
    return Number.NEGATIVE_INFINITY;
  }

  return {
    /**
     * Build a tile.
     *
     * @param {number} originX - Tile's minimum corner, absolute world coordinate.
     * @param {number} originZ
     * @returns {{positions: Float32Array, colors: Float32Array, segmentCount: number,
     *   terrainSegmentCount: number, structureSegmentCount: number, structureCount: number,
     *   minElevation: number, maxElevation: number}}
     */
    /**
     * Every structure a region would hold, without building any geometry.
     *
     * Exposed because cross-biome clearance is a property of the *composition* of biomes, not of any one
     * of them. It can only be checked where the reservations are assembled, and geometry buffers are the
     * wrong place to look for whether a tree ended up inside a tower.
     *
     * @param {{originX: number, originZ: number, size: number}} region
     * @returns {Array<object>} Flattened structure descriptions from every biome.
     */
    collectStructures(region) {
      return collectFor(region).flatMap(({ buildings }) => buildings);
    },

    build(originX, originZ) {
      const terrain = buildTile({
        originX,
        originZ,
        size,
        resolution,
        sampleDetail: (x, z) => heightfield.sampleDetail(x, z),
        palette,
        includeSurface: OCCLUSION.MASK_TERRAIN,
      });

      // Fast path: no biome in this build has structures at all.
      if (builders.length === 0) {
        return {
          ...terrain,
          terrainSegmentCount: terrain.segmentCount,
          structureSegmentCount: 0,
          structureCount: 0,
          mask: terrainMask(terrain),
        };
      }

      const region = { originX, originZ, size };
      const collected = collectFor(region);
      let structureSegments = 0;

      for (const { buildings } of collected) {
        for (const building of buildings) {
          structureSegments += kindOf(building).segments(building);
        }
      }

      if (structureSegments === 0) {
        return {
          ...terrain,
          terrainSegmentCount: terrain.segmentCount,
          structureSegmentCount: 0,
          structureCount: 0,
          mask: terrainMask(terrain),
        };
      }

      const totalSegments = segmentCountFor(resolution) + structureSegments;
      const floats = totalSegments * VERTICES_PER_SEGMENT * COMPONENTS_PER_VERTEX;

      const positions = new Float32Array(floats);
      const colors = new Float32Array(floats);

      // Terrain first, verbatim, so its geometry is unaffected by structures existing.
      positions.set(terrain.positions, 0);
      colors.set(terrain.colors, 0);

      const structurePositions = positions.subarray(terrain.positions.length);
      const structureColors = colors.subarray(terrain.colors.length);
      // Same tile-relative conversion as the mask: emitters think in world coordinates.
      const writer = createGeometryWriter(structurePositions, structureColors, originX, originZ);

      let structureCount = 0;

      for (const { builder, buildings } of collected) {
        for (const building of buildings) {
          kindOf(building).emit(writer, building, builder.gradient, accentColor);
          structureCount += 1;
        }
      }

      return {
        positions,
        colors,
        segmentCount: totalSegments,
        terrainSegmentCount: terrain.segmentCount,
        structureSegmentCount: structureSegments,
        structureCount,
        minElevation: terrain.minElevation,
        maxElevation: terrain.maxElevation,
        mask: combinedMask(terrain, collected),

        // Carried through so the render layer can place the mesh; vertices are tile-relative.
        originX,
        originZ,
      };
    },

    /** Whether any registered biome builds structures. Useful for tests and debug readouts. */
    hasStructures() {
      return builders.length > 0;
    },
  };
}

/** Exported so tests and callers can reason about geometry budgets. */

